import { type ReactNode, useCallback, useEffect, useMemo, useState } from "react";
import useAutenticacaoJwt from "@auth/services/jwt/useAutenticacaoJwt";
import { useQueryClient } from "@tanstack/react-query";
import {
	keys_comunication,
	useGetComunicacaoNotificacoes,
	useGetComunicacaoQuantidadeNaoLidas,
	usePatchComunicacaoNotificacaoLer,
	usePatchComunicacaoNotificacoesLerTodas,
} from "@/api/space/hooks/useSpace.comunication";
import type { IGetComunicacaoNotificacoesRequest } from "@/api/space/dtos/comunication.dto";
import { useQuickPanelActions } from "../../hooks/useQuickPanelActions";
import { useQuickPanelExcelEvent } from "../../hooks/useQuickPanelExcelEvent";
import { useQuickPanelNotificationEvents } from "../../hooks/useQuickPanelNotificationEvents";
import type { QuickPanelMessage } from "../../quickPanel.types";
import { toQuickPanelApiMessage } from "../../utils/quickPanelParsers";
import { QuickPanelContext } from "./QuickPanelContext";

// ------------------------------- CONSTANTS -------------------------------
const NOTIFICACOES_REQUEST: IGetComunicacaoNotificacoesRequest = {};

// -------------------------------- HELPERS --------------------------------
function mesclarMensagensTransitorias(
	messages: QuickPanelMessage[],
	incomingMessages: QuickPanelMessage[],
): QuickPanelMessage[] {
	const incomingIds = new Set(incomingMessages.map((message) => message.id));
	return [...incomingMessages, ...messages.filter((message) => !incomingIds.has(message.id))];
}

type QuickPanelProviderProps = {
	readonly children: ReactNode;
};

// ------------------------------- COMPONENT -------------------------------
export function QuickPanelProvider({ children }: QuickPanelProviderProps) {
	const { isAuthenticated } = useAutenticacaoJwt();
	const queryClient = useQueryClient();
	const [transientMessages, setTransientMessages] = useState<QuickPanelMessage[]>([]);
	const [open, setOpen] = useState(false);

	const notificacoesQuery = useGetComunicacaoNotificacoes(NOTIFICACOES_REQUEST, isAuthenticated && open);
	const { refetch: refetchNotifications } = notificacoesQuery;
	const quantidadeQuery = useGetComunicacaoQuantidadeNaoLidas(isAuthenticated);
	const {
		mutate: markNotificationAsRead,
		isPending: isMarkingNotificationAsRead,
		variables: markNotificationAsReadVariables,
	} = usePatchComunicacaoNotificacaoLer();
	const { mutate: markAllNotificationsAsRead, isPending: isMarkingAllAsRead } =
		usePatchComunicacaoNotificacoesLerTodas();

	const apiMessages = useMemo(
		() => notificacoesQuery.data?.itens.map(toQuickPanelApiMessage) ?? [],
		[notificacoesQuery.data?.itens],
	);
	const messages = useMemo(
		() => mesclarMensagensTransitorias(apiMessages, transientMessages),
		[apiMessages, transientMessages],
	);
	const transientUnreadCount = useMemo(
		() => transientMessages.filter((message) => !message.lido).length,
		[transientMessages],
	);

	const apiUnreadCount =
		quantidadeQuery.data?.quantidade ??
		notificacoesQuery.data?.quantidade_nao_lidas ??
		apiMessages.filter((message) => !message.lido).length;

	const unreadCount = Math.max(0, apiUnreadCount) + transientUnreadCount;
	const messagesLoading = open && notificacoesQuery.isLoading;
	const messagesError = open && notificacoesQuery.isError && !notificacoesQuery.data;

	const addTransientMessages = useCallback((incomingMessages: QuickPanelMessage[]) => {
		if (incomingMessages.length === 0) return;

		setTransientMessages((currentMessages) => mesclarMensagensTransitorias(currentMessages, incomingMessages));
	}, []);

	useQuickPanelNotificationEvents({ enabled: isAuthenticated });
	useQuickPanelExcelEvent({ onMessages: addTransientMessages, enabled: isAuthenticated });
	const { downloadMessageIds, handleAction } = useQuickPanelActions();

	useEffect(() => {
		if (!notificacoesQuery.data) return;

		queryClient.setQueryData(keys_comunication.quantidadeNaoLidas(), {
			quantidade: notificacoesQuery.data.quantidade_nao_lidas,
		});
	}, [notificacoesQuery.data, queryClient]);

	useEffect(() => {
		if (isAuthenticated) return;

		setOpen(false);
		setTransientMessages([]);
	}, [isAuthenticated]);

	const openQuickPanel = useCallback(() => {
		setOpen(true);
	}, []);

	const closeQuickPanel = useCallback(() => {
		setOpen(false);
	}, []);

	const toggleQuickPanel = useCallback(() => {
		setOpen((currentOpen) => !currentOpen);
	}, []);

	const retryMessages = useCallback(() => {
		void refetchNotifications();
	}, [refetchNotifications]);

	const markTransientMessageAsRead = useCallback((messageId: string) => {
		const readAt = new Date();
		setTransientMessages((currentMessages) =>
			currentMessages.map((message) =>
				message.id === messageId && !message.lido ? { ...message, lido: true, lida_em: readAt } : message,
			),
		);
	}, []);

	const markMessageAsRead = useCallback(
		(message: QuickPanelMessage) => {
			if (message.lido) return;

			if (message.origin === "api") markNotificationAsRead({ id_notificacao: message.id });
			else markTransientMessageAsRead(message.id);
		},
		[markNotificationAsRead, markTransientMessageAsRead],
	);

	const executeMessageAction = useCallback(
		(message: QuickPanelMessage) => {
			if (!message.action) return;
			if (!message.lido) markMessageAsRead(message);

			handleAction(message);
		},
		[handleAction, markMessageAsRead],
	);

	const markAllAsRead = useCallback(() => {
		const readAt = new Date();
		setTransientMessages((currentMessages) =>
			currentMessages.map((message) => (message.lido ? message : { ...message, lido: true, lida_em: readAt })),
		);

		if (apiUnreadCount > 0) markAllNotificationsAsRead();
	}, [apiUnreadCount, markAllNotificationsAsRead]);

	const value = useMemo(
		() => ({
			messages,
			unreadCount,
			open,
			loading: {
				messages: messagesLoading,
				messageId: isMarkingNotificationAsRead
					? (markNotificationAsReadVariables?.id_notificacao ?? null)
					: null,
				markAll: isMarkingAllAsRead,
				downloadMessageIds,
			},
			errors: {
				messages: messagesError,
			},
			actions: {
				toggleQuickPanel,
				openQuickPanel,
				closeQuickPanel,
				retryMessages,
				markMessageAsRead,
				executeMessageAction,
				markAllAsRead,
			},
		}),
		[
			messages,
			unreadCount,
			open,
			messagesLoading,
			messagesError,
			isMarkingNotificationAsRead,
			markNotificationAsReadVariables?.id_notificacao,
			isMarkingAllAsRead,
			downloadMessageIds,
			toggleQuickPanel,
			openQuickPanel,
			closeQuickPanel,
			retryMessages,
			markMessageAsRead,
			executeMessageAction,
			markAllAsRead,
		],
	);

	return <QuickPanelContext value={value}>{children}</QuickPanelContext>;
}
